// Row-visibility limits, in one place.
//
// A plan decides how many table rows a user sees for real instead of blurred:
// Free 0, Growth 25, Pro 500, Enterprise everything. Those numbers used to be
// copy-pasted into every table and every image-blur helper, so raising one
// account's ceiling meant a code change and a deploy.
//
// `users.row_limit` is the per-account override, working exactly like
// brand_limit / comp_limit / gap_limit already do: set it and it wins, leave it
// alone and the plan default applies. That lets support widen a single Growth
// or Pro customer without moving them onto another plan.
//
// The column is TEXT and predates this feature. Signup writes 0 into it and
// Stripe writes 'normal', so BOTH are legacy sentinels meaning "no override —
// use the plan default". Reading 0 as a real ceiling of zero rows would blur
// every table for every account created through signup, so 0 is not an
// override. An admin who genuinely wants a customer to see nothing moves them
// to the Free plan.

import { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

export const PLAN_ROW_LIMITS = {
  free: 0,
  growth: 25,
  pro: 500,
  enterprise: Infinity,
};

// Words an admin might reasonably type meaning "no ceiling".
const UNLIMITED_WORDS = new Set(['unlimited', 'unlimted', 'infinite', 'infinity', 'all', '-1']);

// Plans arrive as 'Growth' from some endpoints and 'growth' from others, so
// every lookup normalises first. An unknown plan is treated as Free.
export const planRowLimit = (plan) => {
  const key = String(plan || '').trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(PLAN_ROW_LIMITS, key)
    ? PLAN_ROW_LIMITS[key]
    : PLAN_ROW_LIMITS.free;
};

// null means "no override — fall back to the plan default".
//
// Anything that does not resolve to a row count above zero is treated as "no
// override": blank, 'normal', junk, and — importantly — 0. Signup has always
// written 0 into this column, so reading it as a ceiling would hide every row
// on most accounts in the database. Failing to the plan default is the only
// safe direction here; a bad value must never look like a downgrade.
export const parseRowLimitOverride = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim().toLowerCase();
  if (raw === '' || raw === 'normal' || raw === 'default' || raw === '-') return null;
  if (UNLIMITED_WORDS.has(raw)) return Infinity;
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const resolveRowLimit = (plan, override) => {
  const parsed = parseRowLimitOverride(override);
  return parsed === null ? planRowLimit(plan) : parsed;
};

// The single question every table asks: is this row (index across ALL pages,
// not within the current page) inside the ceiling?
export const isRowVisible = (globalRowIndex, plan, override) =>
  globalRowIndex < resolveRowLimit(plan, override);

// For admin UI and for the older table services, which use `null` to mean
// unlimited rather than Infinity.
export const rowLimitOrNull = (plan, override) => {
  const limit = resolveRowLimit(plan, override);
  return limit === Infinity ? null : limit;
};

export const describeRowLimit = (plan, override) => {
  const limit = resolveRowLimit(plan, override);
  const suffix = parseRowLimitOverride(override) === null ? ' (plan default)' : ' (override)';
  return (limit === Infinity ? 'unlimited' : `${limit} rows`) + suffix;
};

// --- shared override cache -------------------------------------------------
// Nine components already fetch the plan on their own. Rather than add a second
// request to each of them, the override is fetched once per page load and
// shared. An admin change therefore lands on the user's next refresh, not
// mid-session — the same as the plan itself.

let cache = { loaded: false, value: null };
let inflight = null;

// Lets a component that already has the value (e.g. from /api/userprofile) fill
// the cache instead of triggering a second request.
export const primeRowLimitOverride = (value) => {
  cache = { loaded: true, value: value === undefined ? null : value };
};

const loadRowLimitOverride = () => {
  if (cache.loaded) return Promise.resolve(cache.value);
  if (inflight) return inflight;

  inflight = fetch(`${API_BASE}/api/planfetch`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      cache = { loaded: true, value: data && data.rowLimit !== undefined ? data.rowLimit : null };
      return cache.value;
    })
    .catch(() => {
      // Fail to the plan default rather than to zero rows: a network blip must
      // never look like a downgrade.
      cache = { loaded: true, value: null };
      return null;
    })
    .then((value) => {
      inflight = null;
      return value;
    });

  return inflight;
};

export const useRowLimitOverride = () => {
  const [override, setOverride] = useState(cache.loaded ? cache.value : null);

  useEffect(() => {
    let alive = true;
    loadRowLimitOverride().then((value) => {
      if (alive) setOverride(value);
    });
    return () => { alive = false; };
  }, []);

  return override;
};
